import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import { Button, List, Popover, Space } from 'antd'
import { DefaultRenderConfigPanel } from 'features/drilldown/components/table/columnRenders/TableDefaultColumnRender'
import { useOntology } from 'features/dynamicOntology'
import first from 'lodash/first'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'

import { useDrilldownTableConfig } from './DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from './DrilldownTableSchema'

interface ColumnListItemProps {
  column: DrilldownTableColumnDef
  isEditing: boolean
  onEdit?: () => void
  onDelete?: () => void
  onUpdateColumn?: Dispatch<SetStateAction<DrilldownTableColumnDef>>
  onPopoverVisibleChange: (visible: boolean) => void
}

const ColumnListItem: FC<ColumnListItemProps> = ({
  column,
  isEditing,
  onEdit,
  onDelete,
  onUpdateColumn,
  onPopoverVisibleChange,
}) => (
  <List.Item
    actions={[
      ...(onEdit && onUpdateColumn
        ? [
            <Popover
              key='edit'
              content={
                <DefaultRenderConfigPanel
                  columnDef={column}
                  setColumnDef={onUpdateColumn}
                />
              }
              title='Edit Column'
              trigger='click'
              open={isEditing}
              onOpenChange={(visible) => onPopoverVisibleChange(visible)}>
              <Button icon={<EditOutlined />} onClick={() => onEdit()} />
            </Popover>,
          ]
        : []),
      ...(onDelete
        ? [
            <Button
              key='delete'
              icon={<DeleteOutlined />}
              danger
              onClick={() => onDelete()}
            />,
          ]
        : []),
    ]}>
    {column.title}
  </List.Item>
)

export const DrilldownTableColumnListEditor: React.FC = () => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false)
  const { columnDefs, setColumnDefs } = useDrilldownTableConfig()

  const { fields } = useOntology()
  const defaultColumn = useMemo<DrilldownTableColumnDef | undefined>(() => {
    const [field, fieldDef] = first(Object.entries(fields)) ?? [
      undefined,
      undefined,
    ]
    if (!field || !fieldDef) {
      return undefined
    }
    return {
      field,
      title: fieldDef.name,
    }
  }, [fields])

  const handleAddColumn = useCallback(() => {
    if (!defaultColumn) {
      return
    }
    setColumnDefs?.((prevColumns) => [...prevColumns, { ...defaultColumn }])
    setEditingIndex(columnDefs.length) // Set editing index to the new column
    setPopoverVisible(true) // Open popover immediately
  }, [columnDefs.length, defaultColumn, setColumnDefs])

  const handleDeleteColumn = useCallback(
    (index: number) => {
      setColumnDefs?.((prevColumns) =>
        prevColumns.filter((_, i) => i !== index)
      )
    },
    [setColumnDefs]
  )

  const getHandleUpdateColumn: (
    idx: number
  ) => Dispatch<SetStateAction<DrilldownTableColumnDef>> = useCallback(
    (colIdx: number) => (val: SetStateAction<DrilldownTableColumnDef>) => {
      setColumnDefs?.((prevColumns) => {
        const updatedColumn =
          typeof val === 'function' ? val(prevColumns[colIdx]) : val
        return prevColumns.map((col, idx) =>
          idx === colIdx ? updatedColumn : col
        )
      })
    },
    [setColumnDefs]
  )

  const handlePopoverVisibleChange = useCallback(
    (visible: boolean, index: number) => {
      setPopoverVisible(visible)
      setEditingIndex(visible ? index : null)
    },
    []
  )

  const handleEditClick = useCallback((index: number) => {
    setEditingIndex(index)
    setPopoverVisible(true)
  }, [])

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <List
        bordered
        dataSource={[...columnDefs]}
        locale={{ emptyText: 'No columns.' }}
        renderItem={(column, index) => (
          <ColumnListItem
            column={column}
            isEditing={editingIndex === index && popoverVisible}
            onEdit={() => handleEditClick(index)}
            onDelete={() => handleDeleteColumn(index)}
            onUpdateColumn={getHandleUpdateColumn(index)}
            onPopoverVisibleChange={(visible) =>
              handlePopoverVisibleChange(visible, index)
            }
          />
        )}
      />

      {setColumnDefs && (
        <Space
          style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={handleAddColumn}>
            Add Column
          </Button>
        </Space>
      )}
    </div>
  )
}
